import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { InstallAppButton } from "@/components/InstallAppButton";
import {
  LayoutDashboard, BookOpen, Calendar, Users, MessageSquare, Send,
  HandHeart, Sparkles, Info, Settings, LogOut, Menu, X, Heart, Award, Crown, Bell, ShieldCheck, Megaphone, StickyNote, Radio,
} from "lucide-react";

const MEMBER_LINKS = [
  { to: "/about", label: "About", icon: Info },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/devotionals", label: "Devotionals", icon: BookOpen },
  { to: "/events", label: "Events", icon: Calendar },
  { to: "/onair", label: "On the Air", icon: Radio },
  { to: "/directory", label: "Members", icon: Users },
  { to: "/forum", label: "Community Feed", icon: MessageSquare },
  { to: "/notifications", label: "Notifications", icon: Bell, badge: true },
  { to: "/messages", label: "Messages", icon: Send },
  { to: "/prayers", label: "Prayer", icon: HandHeart },
  { to: "/praise", label: "Praise Reports", icon: Sparkles },
  { to: "/assistant", label: "Barnabas AI", icon: Sparkles },
];

const SUPPORTER_LINKS = [
  { to: "/about", label: "About", icon: Info },
  { to: "/supporter", label: "Supporter Home", icon: Heart },
  { to: "/events", label: "Events & Tickets", icon: Calendar },
  { to: "/onair", label: "On the Air", icon: Radio },
  { to: "/devotionals", label: "Devotionals", icon: BookOpen },
  { to: "/forum", label: "Community Feed", icon: MessageSquare },
  { to: "/notifications", label: "Notifications", icon: Bell, badge: true },
  { to: "/messages", label: "Messages", icon: Send },
  { to: "/prayers", label: "Prayer", icon: HandHeart },
  { to: "/praise", label: "Praise Reports", icon: Sparkles },
];

function NavList({ links, pathname, onNavigate, logout, navigate, unread }) {
  return (
    <>
      <nav className="flex-1 py-4 overflow-y-auto">
        {links.map((l) => {
          const active = pathname === l.to;
          return (
            <Link key={l.to} to={l.to} onClick={onNavigate} data-testid={`side-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-6 py-3 transition-colors ${active ? "bg-white/15 text-white" : "text-bone/75 hover:bg-white/10 hover:text-white"}`}>
              <l.icon size={17} strokeWidth={1.6} />
              <span className="font-sans text-sm">{l.label}</span>
              {l.badge && unread > 0 && (
                <span data-testid="notif-badge" className="ml-auto min-w-[20px] h-5 px-1.5 bg-gold text-navy font-mono text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-white/15 py-3">
        <Link to="/account" onClick={onNavigate} data-testid="side-account"
          className={`flex items-center gap-3 px-6 py-3 transition-colors ${pathname === "/account" ? "bg-white/15 text-white" : "text-bone/75 hover:bg-white/10 hover:text-white"}`}>
          <Settings size={17} strokeWidth={1.6} /><span className="font-sans text-sm">Account Settings</span>
        </Link>
        <InstallAppButton className="flex items-center gap-3 px-6 py-3 w-full text-bone/75 hover:bg-white/10 hover:text-white transition-colors" />
        <button onClick={() => { onNavigate?.(); logout(); navigate("/"); }} data-testid="side-logout"
          className="w-full flex items-center gap-3 px-6 py-3 text-bone/75 hover:text-gold transition-colors">
          <LogOut size={17} strokeWidth={1.6} /><span className="font-sans text-sm">Sign Out</span>
        </button>
      </div>
    </>
  );
}

export function AppSidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    api.get("/supporter/status").then((r) => setStatus(r.data)).catch(() => {});
    const poll = () => api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [user]);

  let links = user?.role === "supporter" ? [...SUPPORTER_LINKS] : [...MEMBER_LINKS];
  if (user?.role !== "supporter" && user?.is_supporter) {
    links = [
      MEMBER_LINKS[0],
      { to: "/supporter", label: "Supporter Portal", icon: Heart },
      ...MEMBER_LINKS.slice(1),
    ];
  }
  const isSupporter = status?.is_supporter || user?.role === "supporter" || user?.is_supporter;
  if (isSupporter) {
    if ((status?.level_rank ?? 0) >= 2) links.push({ to: "/circle", label: "Prayer Circle", icon: Crown });
    links.push({ to: "/supporters", label: "Our Supporters", icon: Award });
  }
  links.push({ to: "/notes", label: "Notes", icon: StickyNote });
  if (user?.role === "admin") {
    links.push({ to: "/admin", label: "Admin Console", icon: ShieldCheck });
    links.push({ to: "/marketing", label: "Marketing", icon: Megaphone });
  }

  return (
    <>
      {/* Desktop sidebar */}
      <aside data-testid="app-sidebar" className="hidden md:flex fixed top-0 left-0 h-screen w-64 bg-[#081B54] border-r border-white/10 z-50 flex-col">
        <Link to="/" className="flex items-center px-6 h-24 border-b border-white/15">
          <img src="/logo-light.png" alt="In His Image Movement Ministries" className="h-14 w-auto object-contain" />
        </Link>
        <NavList links={links} pathname={location.pathname} logout={logout} navigate={navigate} unread={unread} />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-bone/90 backdrop-blur-xl border-b border-charcoal/10 py-3 px-5 flex items-center justify-between">
        <Link to="/"><img src="/logo.png" alt="IHIMM" className="h-12 w-auto object-contain" /></Link>
        <button onClick={() => setOpen(true)} data-testid="side-mobile-open" className="text-charcoal relative">
          <Menu strokeWidth={1.5} />
          {unread > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-gold" />}
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="side-drawer">
          <div className="absolute inset-0 bg-charcoal/50" onClick={() => setOpen(false)} />
          <div className="absolute top-0 left-0 h-full w-72 bg-[#081B54] flex flex-col">
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/15">
              <img src="/logo-light.png" alt="IHIMM" className="h-11 w-auto object-contain" />
              <button onClick={() => setOpen(false)} className="text-bone/80 hover:text-white"><X strokeWidth={1.5} /></button>
            </div>
            <NavList links={links} pathname={location.pathname} onNavigate={() => setOpen(false)} logout={logout} navigate={navigate} unread={unread} />
          </div>
        </div>
      )}
    </>
  );
}
